import { useEffect, useRef } from "react";
import { IconX } from "@tabler/icons-react";
import { Button } from "./Button.js";

export interface ActionFeedbackValue {
  tone: "success" | "info" | "error";
  message: string;
}

/** Completed actions expire; failures stay visible until dismissed or retried. */
export function ActionFeedback({
  feedback,
  onDismiss,
}: {
  feedback: ActionFeedbackValue | null;
  onDismiss: () => void;
}) {
  const dismiss = useRef(onDismiss);
  dismiss.current = onDismiss;
  useEffect(() => {
    if (!feedback || feedback.tone === "error") return;
    const timer = window.setTimeout(() => dismiss.current(), 5000);
    return () => window.clearTimeout(timer);
  }, [feedback]);
  if (!feedback) return null;
  const error = feedback.tone === "error";
  return (
    <div
      role={error ? "alert" : "status"}
      aria-live={error ? "assertive" : "polite"}
      className={
        error
          ? "mx-3 mb-3 flex items-start justify-between gap-3 rounded-lg border border-danger/40 bg-danger-subtle px-3 py-2 text-sm text-danger"
          : "fixed bottom-4 right-4 z-50 flex max-w-[min(32rem,calc(100vw-2rem))] items-center gap-3 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground shadow-[var(--surface-shadow)]"
      }
    >
      <span>{feedback.message}</span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        iconOnly
        className="shrink-0"
        aria-label="Dismiss message"
        onClick={onDismiss}
      >
        <IconX size={16} aria-hidden="true" />
      </Button>
    </div>
  );
}
